<template>
    <div>
        <SystemMessage :message="errorMessage" :dismissButton="false" v1 v-if="!!errorMessage"></SystemMessage>
        <div v-if="loadingSchemasList" class="cv-view-page-loader">
            <Loader :status="loadingSchemasList" />
        </div>
        <div v-if="!loadingSchemasList" class="cv-manager-view-container">
            <div class="cv-manager-left-side-container">
                <CVSchemaList v-if="schemas"
                              :schemas="sortedSchemas"
                              :viewOnly="viewOnly"
                              :testerUser="testerUser"
                              @addNew="goToAddNew"
                              @update:selected="goToSchema($event)"
                              :selectedId="selectedSchemaId" />
            </div>
            <NoDataOverlay :isShown="noSchemas"
                           class="no-schemas-overlay"
                           icon="noDocumentsIcon"
                           mainText="No Conversion Value Map Defined"
                           mainTextClass="font-3 no-data-overlay-main-text">
                <div slot="subText" class="font-7 no-data-overlay-subtext">
                    To get started, create a new CV map by clicking on the + button
                </div>
            </NoDataOverlay>
            <div v-if="loadingSelectedSchema" class="cv-view-page-selected-schema-loader">
                <Loader :status="loadingSelectedSchema" />
            </div>
            <div v-if="!noSchemas && !loadingSelectedSchema" class="cv-manager-right-side-container"
                 ref="cvMapContainer">
                <div class="fixed-header">
                    <div class="background-cover"></div>
                    <CVManagerViewHeader
                        class="cv-view-header"
                        v-if="selectedSchema"
                        :schema="selectedSchema"
                        @delete="deleteSchemaPrompt"
                        @edit="goToEdit"
                    />
                    <CVWizardHeader />
                </div>
                <div class="content">
                    <CVManagerCVMap
                        v-if="selectedSchema"
                        :steps.sync="selectedSchema.steps"
                        :readonly="true"
                        :apps="appKeys"
                        :cvLoaded="cvLoaded"
                        :scrollerRef="cvMapContainer"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';
import CVSchemaList from 'pages/conversionValue/cvManager/components/CVSchemaList.vue';
import NoDataOverlay from 'components/noDataOverlay/noDataOverlay.vue';
import CVWizardHeader from 'pages/conversionValue/cvManager/components/CVWizardHeader.vue';
import SystemMessage from 'components/SystemMessage/SystemMessage.vue';
import {
    apiKey,
    apiPath,
    formatCVMapResponse,
    getDataForIdAPIPath
} from 'pages/conversionValue/cvManager/serviceUtils';
import consts from 'services/metaData/consts';
import http from 'services/utils/http';
import Loader from 'components/loader/loader.vue';
import CVManagerViewHeader from 'pages/conversionValue/cvManager/components/CVManagerViewHeader.vue';
import PageTooltip from 'components/Tooltip/PageTooltip.vue';
import links from 'services/metaData/links';
import authService from 'services/auth';
import monetizeAppService from 'services/monetizeApp';
import CVManagerCVMap from '../components/CVManagerCVMap.vue';

const cvManagerUrl = '/conversionValue/cvManager';

export default {
    name: 'CVManagerViewPage',
    components: {
        Loader,
        CVWizardHeader,
        NoDataOverlay,
        CVSchemaList,
        CVManagerViewHeader,
        CVManagerCVMap,
        SystemMessage
    },
    props: {
        selectedSchemaId: String
    },
    data() {
        return {
            schemas: undefined,
            selectedSchema: null,
            errorMessage: null,
            loadingSchemasList: false,
            loadingSelectedSchema: false,
            viewOnly: true,
            testerUser: true,
            cvLoaded: false,
            cvMapContainer: null
        };
    },
    async beforeMount() {
        [this.viewOnly, this.testerUser] = await Promise.all([authService.isAuthorized(['viewer']),
            authService.isAuthorized(['monetizerTester'])]);
    },
    async created() {
        this.setPage({
            pageTitle: 'Ad Revenue Conversion Value Manager',
            controlsComponent: PageTooltip,
            controlsComponentProps: {
                title: 'Ad Revenue Conversion Value Manager',
                text: 'Map the value of your users based on the ad revenue they generated in the first'
                    + ' 24 hours since they opened your app.\n\n'
                    + 'Note: Support is available starting SDK version 7.1.1',
                link: links.cvManager
            }
        });
        await this.getSchemas();
        await this.handleInvalidSchemaId();

        if (this.selectedSchemaId) {
            await this.getSchema(this.selectedSchemaId);
        }
    },
    computed: {
        appKeys() {
            return this.selectedSchema?.apps.map((app) => app.appKey) ?? [];
        },
        noSchemas() {
            return !this.schemas?.length;
        },
        sortedSchemas() {
            return this.schemas?.slice().sort((a, b) => a.name.localeCompare(b.name)) ?? [];
        }
    },
    methods: {
        ...mapMutations([
            'setPage'
        ]),
        ...mapActions({
            getWithCache: 'cache/getWithCache'
        }),
        goToFirstSchema() {
            if (!this.schemas.length) {
                return this.$router.replace(cvManagerUrl);
            }

            return this.$router.replace(`${cvManagerUrl}/${this.sortedSchemas[0].id}`);
        },
        goToSchema(id) {
            return this.$router.replace(`${cvManagerUrl}/${id}`);
        },
        goToAddNew() {
            return this.$router.push(`${cvManagerUrl}/new/edit`);
        },
        goToEdit() {
            return this.$router.push(`${cvManagerUrl}/${this.selectedSchemaId}/edit`);
        },
        isValidSchemaId(id) {
            // eslint-disable-next-line eqeqeq
            return !!this.schemas.find((schema) => schema.id == id);
        },
        async handleInvalidSchemaId() {
            if (this.selectedSchemaId) {
                if (this.schemas.length === 0) {
                    await this.$router.replace(cvManagerUrl);
                } else if (!this.isValidSchemaId(this.selectedSchemaId)) {
                    await this.goToFirstSchema();
                }
            } else if (!this.selectedSchemaId && this.schemas && this.schemas.length > 0) {
                await this.goToFirstSchema();
            }
        },
        async getSchemas(refresh = false) {
            try {
                this.loadingSchemasList = true;
                const response = await this.getWithCache({
                    api: apiKey,
                    req: apiPath,
                    refresh
                });
                this.schemas = response.data;
                this.loadingSchemasList = false;
            } catch (error) {
                this.errorMessage = error.response?.data?.errorMessage || consts.GENERAL_SERVICE_ERROR;
                this.loadingSchemasList = false;
            }
        },
        async getSchema(id) {
            try {
                this.loadingSelectedSchema = true;
                const response = await this.getWithCache({
                    api: apiKey,
                    req: getDataForIdAPIPath(id)
                });
                this.selectedSchema = { ...response.data, steps: formatCVMapResponse(response.data.steps) };
                this.cvLoaded = true;
            } catch (error) {
                this.errorMessage = error.response?.data?.errorMessage || consts.GENERAL_SERVICE_ERROR;
            }
            this.loadingSelectedSchema = false;
            await this.$nextTick();
            this.cvMapContainer = this.$refs.cvMapContainer;
        },
        deleteSchemaPrompt() {
            this.$modal.open({
                title: 'Delete CV Map',
                body: `Are you sure you want to delete the "${this.selectedSchema.name}" CV map?`,
                type: 'general',
                version: 1,
                okButtonText: 'Yes, delete',
                okButtonWidth: 120,
                onOK: async () => {
                    await http.delete(`${apiPath}/${this.selectedSchemaId}`);
                    await this.getSchemas(true);
                    monetizeAppService.clearData();
                    this.goToFirstSchema();
                }
            });
        }
    },
    watch: {
        selectedSchemaId: {
            handler(newSchemaId) {
                if (newSchemaId && this.schemas && this.isValidSchemaId(newSchemaId)) {
                    this.selectedSchema = null;
                    this.getSchema(newSchemaId);
                }
            },
            immediate: true
        }
    }
};
</script>

<style scoped lang="scss">
@import "~styles/styleGuide";
@import "src/components/layout/common";

.cv-manager-view-container {
    display: grid;
    grid-template-columns: 3fr 9fr;
    grid-column-gap: 20px;
    padding-left: 24px;
    height: 100%;

    @media #{$desktopAndAbove} {
        padding-left: 40px;
        grid-column-gap: 32px;
    }
}

.cv-view-page-loader {
    position: relative;
    height: 100%;
}

.cv-view-page-selected-schema-loader {
    position: relative;
    height: calc(100% - 24px);
}

.fixed-header {
    position: sticky;
    top: 0;
    z-index: 1;
}

.content {
    position: relative;
    margin-bottom: 24px;
}

.no-schemas-overlay {
    position: relative;

    ::v-deep .no-data-overlay-main-text {
        color: $light-700;
        padding-bottom: 0;
        height: 18px;
        line-height: normal;
    }

    ::v-deep .noData-wrapper {
        height: inherit;
        bottom: 24px;
        top: 24px;
        right: 24px;
        border-radius: 8px;

        @media #{$desktopAndAbove} {
            right: 40px;
        }
    }
}

.no-data-overlay-subtext {
    color: $light-800;
    height: 17px;
    line-height: normal;
    margin-top: 5px;
}

.cv-manager-right-side-container {
    display: flex;
    flex-direction: column;
    overflow: auto;
    padding-right: 24px;
    padding-top: 24px;
    @include pageContentHeight;

    @media #{$desktopAndAbove} {
        padding-right: 40px;
    }
}

//set width to left container, for ellipsis on long schema titles
.cv-manager-left-side-container {
    width: 100%;
    height: 100%;
    overflow: hidden;
    padding-top: 24px;
}

.cv-view-header {
    height: 121px;
}

.background-cover {
    height: 24px;
    margin-top: -24px;
    background-color: $light-200;
}
</style>
